import {
  getOperationKey,
  type AsyncOperation,
  type AsyncOperationSummary,
  type OperationKey,
  type OperationScope,
} from '@vrplatform/data';
import { createContext } from 'react';
import { createStore, type StoreApi } from 'zustand';
import type {
  OperationEffect,
  OperationStoppedStatus,
  OperationSubmission,
  TrackOperationGroupInput,
  TrackOperationInput,
  TrackedOperation,
} from './operationTracker.types';

type OperationGroupRecord = Readonly<{
  groupId: string;
  operationKeys: readonly OperationKey[];
  effects: readonly OperationEffect[];
  claimedEffectIds: readonly string[];
}>;

type RecoverOperationInput = Readonly<{
  operation: AsyncOperationSummary;
  scope: OperationScope;
  effects?: readonly OperationEffect[];
}>;

export type OperationTrackerState = {
  operations: Readonly<Record<OperationKey, TrackedOperation>>;
  groups: Readonly<Record<string, OperationGroupRecord>>;
  trackOperation: (input: TrackOperationInput) => OperationKey;
  recoverOperation: (input: RecoverOperationInput) => OperationKey;
  updateOperation: (key: OperationKey, operation: AsyncOperation) => void;
  stopOperation: (
    key: OperationKey,
    status: Extract<OperationStoppedStatus, 'unavailable' | 'error'>
  ) => void;
  claimOperationEffect: (key: OperationKey, effectId: string) => boolean;
  trackOperationGroup: (input: TrackOperationGroupInput) => void;
  claimGroupEffect: (groupId: string, effectId: string) => boolean;
  submissions: Readonly<Record<string, OperationSubmission>>;
  /** Storage that mirrors `submissions`; null until hydrated. */
  submissionsPersistence: SubmissionsPersistence | null;
  hydrateSubmissions: (
    persistence: SubmissionsPersistence,
    submissions: readonly OperationSubmission[]
  ) => void;
  saveSubmission: (submission: OperationSubmission) => void;
  removeSubmission: (idempotencyKey: string) => void;
  reset: () => void;
};

export type SubmissionsPersistence = Readonly<{
  storage: Pick<Storage, 'removeItem' | 'setItem'>;
  key: string;
}>;

const persistSubmissions = (
  persistence: SubmissionsPersistence | null,
  submissions: OperationTrackerState['submissions']
) => {
  if (!persistence) {
    throw new Error('Operation submissions are not hydrated for this session');
  }

  const values = Object.values(submissions);
  if (values.length) {
    persistence.storage.setItem(persistence.key, JSON.stringify(values));
  } else {
    persistence.storage.removeItem(persistence.key);
  }
};

const mergeEffects = (
  current: readonly OperationEffect[],
  incoming: readonly OperationEffect[] | undefined
) => {
  if (!incoming?.length) return current;

  const effectsById = new Map(current.map((effect) => [effect.id, effect]));
  for (const effect of incoming) {
    if (!effect.id || effect.id.startsWith('\0')) {
      throw new Error('Operation effects require a non-reserved stable ID');
    }
    if (
      'target' in effect &&
      effect.target.scope === 'team' &&
      !effect.target.teamIds.length
    ) {
      throw new Error('Team operation effects require at least one team ID');
    }
    if (effect.kind === 'api' && !effect.groups.length) {
      throw new Error('API operation effects require at least one query group');
    }
    if (effect.kind === 'hasura' && !effect.queryKeys.length) {
      throw new Error(
        'Hasura operation effects require at least one query key'
      );
    }
    if (
      effect.kind === 'notification' &&
      effect.action &&
      effect.status !== 'completed'
    ) {
      throw new Error(
        'Operation notification actions require completed status'
      );
    }

    const existing = effectsById.get(effect.id);
    // A notification icon is a React element, which is not comparable as JSON.
    const declaration = (value: OperationEffect) =>
      JSON.stringify(value, (key, field) =>
        key === 'icon' ? undefined : field
      );
    if (existing && declaration(existing) !== declaration(effect)) {
      throw new Error(
        `Operation effect "${effect.id}" was registered with conflicting declarations`
      );
    }

    effectsById.set(effect.id, effect);
  }

  return [...effectsById.values()];
};

const mergeKeys = (
  current: readonly OperationKey[],
  incoming: readonly OperationKey[]
) => [...new Set([...current, ...incoming])];

const isStopped = (status: TrackedOperation['status']) =>
  status === 'completed' ||
  status === 'failed' ||
  status === 'unavailable' ||
  status === 'error';

const createTrackedOperation = ({
  acknowledgement,
  scope,
  effects,
}: TrackOperationInput): TrackedOperation => {
  const reference = { ...scope, operationId: acknowledgement.operationId };
  const key = getOperationKey(reference);

  return {
    key,
    reference,
    operation: acknowledgement,
    status: acknowledgement.status,
    resource: acknowledgement.resource,
    failure: null,
    effects: mergeEffects([], effects),
    claimedEffectIds: [],
    source: 'acknowledgement',
    updatedAt: Date.now(),
  };
};

const createRecoveredOperation = ({
  operation,
  scope,
  effects,
}: RecoverOperationInput): TrackedOperation => {
  const reference = { ...scope, operationId: operation.id };
  const key = getOperationKey(reference);

  return {
    key,
    reference,
    operation,
    status: operation.status,
    resource: operation.resource,
    failure: operation.failure,
    effects: mergeEffects([], effects),
    claimedEffectIds: [],
    source: 'summary',
    updatedAt: Date.now(),
  };
};

export const createOperationTrackerStore = () =>
  createStore<OperationTrackerState>()((set, get) => ({
    operations: {},
    groups: {},
    trackOperation: (input) => {
      const incoming = createTrackedOperation(input);
      const existing = get().operations[incoming.key];

      if (existing) {
        set((state) => ({
          operations: {
            ...state.operations,
            [incoming.key]: {
              ...existing,
              effects: mergeEffects(existing.effects, input.effects),
            },
          },
        }));
        return incoming.key;
      }

      set((state) => ({
        operations: { ...state.operations, [incoming.key]: incoming },
      }));
      return incoming.key;
    },
    recoverOperation: (input) => {
      const incoming = createRecoveredOperation(input);
      const existing = get().operations[incoming.key];

      if (existing) {
        set((state) => ({
          operations: {
            ...state.operations,
            [incoming.key]: {
              ...existing,
              effects: mergeEffects(existing.effects, input.effects),
              ...(isStopped(existing.status)
                ? {}
                : {
                    operation: incoming.operation,
                    status: incoming.status,
                    resource: incoming.resource,
                    failure: incoming.failure,
                    source: incoming.source,
                    updatedAt: incoming.updatedAt,
                  }),
            },
          },
        }));
        return incoming.key;
      }

      set((state) => ({
        operations: { ...state.operations, [incoming.key]: incoming },
      }));
      return incoming.key;
    },
    updateOperation: (key, operation) => {
      const existing = get().operations[key];
      if (!existing) return;

      set((state) => ({
        operations: {
          ...state.operations,
          [key]: {
            ...existing,
            operation,
            status: operation.status,
            resource: operation.resource,
            failure: operation.failure,
            source: 'detail',
            updatedAt: Date.now(),
          },
        },
      }));
    },
    stopOperation: (key, status) => {
      const existing = get().operations[key];
      if (!existing || isStopped(existing.status)) return;

      set((state) => ({
        operations: {
          ...state.operations,
          [key]: { ...existing, status, updatedAt: Date.now() },
        },
      }));
    },
    claimOperationEffect: (key, effectId) => {
      const existing = get().operations[key];
      if (!existing || existing.claimedEffectIds.includes(effectId)) {
        return false;
      }

      set((state) => ({
        operations: {
          ...state.operations,
          [key]: {
            ...existing,
            claimedEffectIds: [...existing.claimedEffectIds, effectId],
          },
        },
      }));
      return true;
    },
    trackOperationGroup: ({ groupId, operationKeys, effects }) => {
      if (!groupId) throw new Error('Operation group requires an ID');
      if (!operationKeys.length) {
        throw new Error('Operation group requires at least one operation');
      }
      if (operationKeys.some((key) => !key)) {
        throw new Error('Operation group contains an invalid operation key');
      }

      const existing = get().groups[groupId];
      set((state) => ({
        groups: {
          ...state.groups,
          [groupId]: {
            groupId,
            operationKeys: mergeKeys(
              existing?.operationKeys ?? [],
              operationKeys
            ),
            effects: mergeEffects(existing?.effects ?? [], effects),
            claimedEffectIds: existing?.claimedEffectIds ?? [],
          },
        },
      }));
    },
    claimGroupEffect: (groupId, effectId) => {
      const existing = get().groups[groupId];
      if (!existing || existing.claimedEffectIds.includes(effectId)) {
        return false;
      }

      set((state) => ({
        groups: {
          ...state.groups,
          [groupId]: {
            ...existing,
            claimedEffectIds: [...existing.claimedEffectIds, effectId],
          },
        },
      }));
      return true;
    },
    submissions: {},
    submissionsPersistence: null,
    hydrateSubmissions: (persistence, submissions) =>
      set({
        submissions: Object.fromEntries(
          submissions.map((submission) => [
            submission.idempotencyKey,
            submission,
          ])
        ),
        submissionsPersistence: persistence,
      }),
    saveSubmission: (submission) => {
      const submissions = {
        ...get().submissions,
        [submission.idempotencyKey]: submission,
      };

      persistSubmissions(get().submissionsPersistence, submissions);
      set({ submissions });
    },
    removeSubmission: (idempotencyKey) => {
      if (!get().submissions[idempotencyKey]) return;

      const { [idempotencyKey]: _removed, ...submissions } = get().submissions;

      persistSubmissions(get().submissionsPersistence, submissions);
      set({ submissions });
    },
    // Session storage is left intact: it belongs to the signed-in session and
    // is re-read on the next hydrate. Other sessions' entries are dropped then.
    reset: () =>
      set({
        operations: {},
        groups: {},
        submissions: {},
        submissionsPersistence: null,
      }),
  }));

export type OperationTrackerStore = ReturnType<
  typeof createOperationTrackerStore
>;

export const OperationTrackerContext =
  createContext<StoreApi<OperationTrackerState> | null>(null);

export const OperationTrackerLifetimeContext =
  createContext<AbortSignal | null>(null);
